import { useEffect, useState } from "react";
import { useController } from "react-hook-form";

import TextInput from "@/form/inputs/TextInput/TextInput";
import { useFieldIds } from "@/form/useFieldIds";
import { cn } from "@/lib/utils";

import FieldError from "../FieldError/FieldError";
import FieldHint from "../FieldHint/FieldHint";
import FieldLabel from "../FieldLabel/FieldLabel";

type TimePickerFieldProps = {
  className?: string;
  disabled?: boolean;
  name: string;
  label: string;
  placeholder?: string;
  hint?: string;
  hideValidation?: boolean;
  hideLabel?: boolean;
  minTime?: string;
  maxTime?: string;
};

export default function TimePickerField({
  disabled,
  name,
  label,
  className,
  placeholder = "HH:MM",
  hint,
  hideValidation,
  hideLabel,
  minTime,
  maxTime,
}: TimePickerFieldProps): React.ReactElement {
  const { inputId, labelId, errorId, describedBy, hintId } = useFieldIds(name);
  const { field, fieldState } = useController({
    name,
  });

  const [displayValue, setDisplayValue] = useState(
    field.value ? formatTimeForDisplay(field.value) : ""
  );

  const invalid = !!fieldState.error;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value === "") {
      setDisplayValue("");
      field.onChange("");
      return;
    }

    // Store the raw value (digits only)
    const rawValue = value.replace(/\D/g, "");
    field.onChange(rawValue);

    // Display the formatted value
    const formatted = formatTimeForDisplay(rawValue);
    setDisplayValue(formatted);
  };

  const handleBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (!value) {
      setDisplayValue("");
    } else {
      // Ensure we have a properly formatted display value on blur
      const rawValue = value.replace(/\D/g, "");
      const formatted = formatTimeForDisplay(rawValue);
      setDisplayValue(formatted);

      // Convert to 24-hour format for storage
      if (formatted.length === 5) {
        field.onChange(formatted);
      }
    }
  };

  useEffect(() => {
    if (field.value && field.value !== displayValue.replace(/\D/g, "")) {
      setDisplayValue(formatTimeForDisplay(field.value));
    }
  }, [field.value]);

  return (
    <div
      className={cn("TimePickerField grid flex-1 gap-2", hideLabel && "hidden")}
      data-testid="TimePickerField"
      data-field={name}
    >
      <div className={cn("grid", hideLabel && "sr-only")}>
        <FieldLabel
          htmlFor={inputId}
          id={labelId}
          invalid={invalid}
          value={label}
        />
      </div>

      <TextInput
        aria-describedby={describedBy(!!hint, !!fieldState.error)}
        aria-invalid={invalid}
        className={className}
        disabled={disabled}
        name={name}
        placeholder={placeholder}
        value={displayValue}
        onBlur={handleBlur}
        onChange={handleChange}
        type="time"
      />

      <div
        className={cn(
          "align-center flex h-3 justify-start",
          hideValidation && "hidden"
        )}
      >
        {hint && !fieldState.error && <FieldHint hint={hint} id={hintId} />}
        {fieldState.error && (
          <FieldError error={fieldState.error} id={errorId} />
        )}
      </div>
    </div>
  );
}

const formatTimeForDisplay = (timeValue: string): string => {
  if (!timeValue) return "";

  // Remove any existing formatting and non-digits
  const cleaned = timeValue.replace(/\D/g, "");

  if (cleaned.length === 0) return "";
  if (cleaned.length <= 2) return cleaned;
  if (cleaned.length <= 4) {
    return `${cleaned.slice(0, 2)}:${cleaned.slice(2)}`;
  }

  // Limit to 4 digits (HH:MM)
  const limited = cleaned.slice(0, 4);
  return `${limited.slice(0, 2)}:${limited.slice(2)}`;
};

const isValidTime = (timeString: string): boolean => {
  if (!timeString || timeString.length !== 5) return false;

  const [hours, minutes] = timeString.split(":").map(Number);

  if (isNaN(hours) || isNaN(minutes)) return false;
  if (hours < 0 || hours > 23) return false;
  if (minutes < 0 || minutes > 59) return false;

  return true;
};
